import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsExporting from 'highcharts/modules/exporting';
import highchartsExportData from 'highcharts/modules/export-data';
import highchartsHeatmap from 'highcharts/modules/heatmap';
import noDataToDisplay from 'highcharts/modules/no-data-to-display';
import { useAppContext } from '../../contexts/AppContext';
import { useEffect, useRef, useState } from 'react';
import { Backdrop, Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import { compare_name_asc } from '../../utils/utils';
dayjs.extend(dayOfYear);

highchartsExporting(Highcharts);
highchartsExportData(Highcharts);
highchartsHeatmap(Highcharts);
noDataToDisplay(Highcharts);

type Props = {
  data: any;
};

const CompareProductionLosses = ({ data }: Props) => {
  const { isDarkMode } = useAppContext();
  const [chartOptions, setChartOptions] = useState<any>(null);
  const chartComponent = useRef(null);
  const [isLoading] = useState(false);

  useEffect(() => {
    if (data && data.length > 0) {
      prepareHighcharts(prepareData());
    }
  }, [data]);

  const prepareData = () => {
    const categories = data?.[0]?.data?.monthly?.times || [];
    const timestamps = categories.map((dateStr: string) => new Date(dateStr).getTime());

    return data.sort(compare_name_asc).map((item: any) => {
      const values = item?.data.monthly.values.production_losses || [];
      const dataWithTimestamps = values.map((value: number, index: number) => [timestamps[index], value]);

      return {
        name: item.name,
        data: dataWithTimestamps,
      };
    });
  };

  const prepareHighcharts = (highChartsData: any) => {
    const chart = {
      chart: {
        type: 'column',
        zoomType: 'x',
        panning: {
          enabled: true,
          type: 'x',
        },
        panKey: 'shift',
      },
      title: {
        text: 'Production Losses',
        align: 'left',
      },
      xAxis: {
        type: 'datetime',
        title: { text: 'Date' },
        labels: {
          rotation: 0,
          style: {
            fontSize: '12px',
          },
          formatter: function (this: any): string {
            const d = new Date(this.value);
            if (data && data.length > 1) {
              return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2);
            }
            return ('0' + (d.getMonth() + 1)).slice(-2);
          },
        },
        gridLineWidth: 1,
      },
      yAxis: {
        title: { text: 'Production Losses (MWh/month)' },
      },
      tooltip: {
        formatter: function (this: any): string {
          const d = new Date(this.x);
          const dateFormat =
            data && data.length > 1 ? d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) : ('0' + (d.getMonth() + 1)).slice(-2);
          return dateFormat + '<br><b>' + this.series.name + '</b>: ' + Math.round(this.point.y * 100) / 100 + ' MWh';
        },
      },
      credits: {
        enabled: false,
      },
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0.2,
          borderWidth: 0,
        },
      },
      series: highChartsData,
    };
    setChartOptions(chart);
  };

  return (
    <>
      <Box sx={{ /*display: 'flex',*/ justifyContent: 'center', pl: 1, pr: 1 }}>
        {isLoading ? (
          <Backdrop open={true} sx={{ width: '100%', height: '450px', position: 'relative' }}>
            <CircularProgress />
          </Backdrop>
        ) : (
          data &&
          chartOptions && (
            <>
              <HighchartsReact
                ref={chartComponent}
                containerProps={{ className: isDarkMode ? 'dark-chart' : '', style: { width: '100%' } }}
                highcharts={Highcharts}
                options={chartOptions}
              />
            </>
          )
        )}
      </Box>
    </>
  );
};

export default CompareProductionLosses;
